import {PIXI} from '@colonist/pixi'
import {UIGameManager} from '../../ui_game/UIGameManager'
import {UIGameCardWithRatioContainerView} from '../../ui_game/ui_game_card/UIGameCardWithRatioContainerView'
import {SectionView} from '../../ui_canvas_engine/SectionView'
import {SpriteView} from '../../ui_canvas_engine/SpriteView'
import {FloatingText} from '../../game_ui_view_objects/FloatingText'
import {ArrayUtils} from '@colonist/utils'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {getMerchantImageNameForPlayer, Sprites} from '../../ui_game/UIGameImageDictionary'
import {UIGameTradeBeginnerCardAnimationsController} from '../UIGameTradeBeginnerCardAnimationsController'
import {UIGameShakeAnimation} from '../../ui_canvas_engine/animation/UIGameShakeAnimation'
import {UIGameAnimationBase} from '../../ui_canvas_engine/animation/UIGameAnimationBase'
import {CardEnum} from '@colonist/game-states'
import {TradeUtils} from '@colonist/game-mechanic-validators'
import {CardDataController} from '@colonist/game-card-types'

export class UIGameTradeOfferedCardSelectorView extends SectionView {
    private readonly uiGameManager: UIGameManager

    beginnerCardAnimationController: UIGameTradeBeginnerCardAnimationsController

    private merchantIcon: SpriteView | undefined
    readonly cardsContainer: UIGameCardWithRatioContainerView

    private bankIconAnimation: UIGameAnimationBase

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, container: PIXI.Container) {
        super(x, y, w, h, container, true)

        this.uiGameManager = uiGameManager

        this.cardsContainer = new UIGameCardWithRatioContainerView(this.uiGameManager, 0, 0, w, h, this)
        this.beginnerCardAnimationController = new UIGameTradeBeginnerCardAnimationsController(this.uiGameManager, this.cardsContainer.cardInventory.cardViews)
        this.cardsContainer.setCardClickAction((cardEnum: CardEnum) => {
            if(this.uiGameManager.tradeController.inBankTradeMode) {
                this.onCardClickBankTrade(cardEnum)
                return
            }
            this.onCardClickPlayersTrade(cardEnum)
        })

        this.cardsContainer.setCardRightClickAction((cardEnum: CardEnum) => {
            this.onCardClickBankTrade(cardEnum)
        })
    }

    resetCards(isAnyResourceCardEnabled: boolean = false) {
        const animatingCards = this.beginnerCardAnimationController.cardEnumsForBeginnerCardAnimation
        this.beginnerCardAnimationController.stopBeginnerAnimation()
        this.resetMerchantIcon()
        this.cardsContainer.removeAllCards()
        const playerColor = this.uiGameManager.gameController.myColor

        const allPlayerCards = this.uiGameManager.gameController.getAllCardsInHand(playerColor)
        if(isAnyResourceCardEnabled) allPlayerCards.push(CardEnum.AnyResource)

        for(const card of allPlayerCards) {
            const cardView = this.cardsContainer.addNewCardOfType(card, false)

            const cardData = CardDataController.getCardDataForCard(card)
            if(cardData?.cardEnum === CardEnum.AnyResource) continue
            if(cardData && CardDataController.isResourceCard(cardData.cardEnum)) continue

            cardView.fadeOutCard()
            cardView.view.removeClickAction()
            cardView.view.setClickAction(() => {
                new FloatingText({
                    key: 'strings:game.tooltips.cannotTradeDevCard',
                    options: {
                        context: this.uiGameManager.gameState.gameModeData.translationKeyContext,
                    },
                }, cardView.view)
            })
        }

        this.beginnerCardAnimationController = new UIGameTradeBeginnerCardAnimationsController(this.uiGameManager, this.cardsContainer.cardInventory.cardViews)
        this.beginnerCardAnimationController.startBeginnerAnimation(animatingCards)
    }

    createCounterOffer(offeredResources: CardEnum[]) {
        const cardsInHand = this.uiGameManager.gameController.getAllCardsInHand(this.uiGameManager.gameController.myColor)
        for(const offeredResource of offeredResources) {
            if(!ArrayUtils.elementInArray(cardsInHand, offeredResource)) continue
            this.onCardClickPlayersTrade(offeredResource)
            ArrayUtils.removeElementFromArray(cardsInHand, offeredResource)
        }
    }

    addMerchantIconToCardView(cardEnum: CardEnum) {
        const cardView = this.cardsContainer.cardInventory.getTopCardWithType(cardEnum)
        if(cardView == undefined) return

        const merchantTextureImageName = getMerchantImageNameForPlayer(this.uiGameManager.gameController.myColor)
        const merchantTexture = UIGameSpriteHandler.getTextureFromSpritesheet(Sprites[merchantTextureImageName])
        const merchantIconSize = cardView.width * 0.55
        const merchantIconX = cardView.x + cardView.width - merchantIconSize * 0.7
        const merchantIconY = cardView.height - merchantIconSize * 0.5
        this.merchantIcon = new SpriteView(merchantIconX, merchantIconY, merchantIconSize, merchantIconSize, this, merchantTexture)
    }

    private resetMerchantIcon() {
        if(this.merchantIcon == undefined) return
        if(this.merchantIcon.parent == undefined) return
        this.merchantIcon.parent.removeChild(this.merchantIcon)
    }

    onCardClickPlayersTrade(cardEnum: CardEnum) {
        if(cardEnum == CardEnum.AnyResource) {
            this.onCardClickAnyResourceCard()
            return
        }

        this.cardsContainer.removeCard(cardEnum)
        this.uiGameManager.tradeController.proposalView.offeredCardPlayerInfo.cardContainer.addNewCardOfType(cardEnum)
    }

    onCardClickAnyResourceCard() {
        if(this.uiGameManager.tradeController.proposalView.offeredCardPlayerInfo.containsAnyResourceCard()) return
        this.uiGameManager.tradeController.proposalView.offeredCardPlayerInfo.cardContainer.addNewCardOfType(CardEnum.AnyResource)
    }

    onCardClickBankTrade(cardEnum: CardEnum) {
        const bankTradeRatios = this.uiGameManager.gameController.getPlayerBankTradeRatios(this.uiGameManager.gameController.myColor)
        const requiredCardAmount = TradeUtils.getBankTradeRatioForCard(bankTradeRatios, cardEnum)
        const currentCardAmountInHand = this.cardsContainer.cardInventory.getCardTypeCount(cardEnum)
        const currentCardAmountInProposal = this.uiGameManager.tradeController.proposalView.offeredCardPlayerInfo.cardContainer.cardInventory.getCardTypeCount(cardEnum) % requiredCardAmount

        const canAffordRatio = currentCardAmountInHand + currentCardAmountInProposal >= requiredCardAmount
        if(!canAffordRatio) {
            this.disableBankTradeModeIfClickedFadedCard(cardEnum)
            return
        }

        const totalCardsToAdd = requiredCardAmount - currentCardAmountInProposal
        for(let i = 0; i < totalCardsToAdd; i++) {
            this.cardsContainer.removeCard(cardEnum)
            this.uiGameManager.tradeController.proposalView.offeredCardPlayerInfo.cardContainer.addNewCardOfType(cardEnum)
        }
    }

    private disableBankTradeModeIfClickedFadedCard(originalCardEnum: CardEnum) {
        this.uiGameManager.tradeController.toggleBankTradeMode(false)
        // TODO: Move to central animation controller
        if(this.bankIconAnimation != undefined) this.bankIconAnimation.stop()
        this.bankIconAnimation = new UIGameShakeAnimation(this.uiGameManager.tradeController.wantedCardSelectorView.bankTradeModeIcon).start()
        this.onCardClickPlayersTrade(originalCardEnum)
    }
}
